import React, { useState, useRef, useCallback } from 'react';
import {
    Box,
    Button,
    Card,
    CardMedia,
    IconButton,
    Typography,
    CircularProgress,
    Alert,
    Paper,
} from '@mui/material';
import {
    CloudUpload,
    Delete,
    Image as ImageIcon,
    Close
} from '@mui/icons-material';
import { useTranslate, useNotify } from 'react-admin';
import { API_CONFIG } from '../../config/api';
import { tokenManager } from '../../utils/tokenManager';

interface ImageUploadFieldProps {
    source: string;
    value?: string;
    onChange: (value: string | null) => void;
    label?: string;
    helperText?: string;
}

const ImageUploadField: React.FC<ImageUploadFieldProps> = ({
    source,
    value,
    onChange,
    label,
    helperText
}) => {
    const translate = useTranslate();
    const notify = useNotify();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState<string | null>(value || null);
    const [error, setError] = useState<string | null>(null);

    const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');

                    // Target aspect ratio is 2:1 (width:height)
                    const targetAspectRatio = 2;
                    const targetWidth = maxWidth;
                    const targetHeight = maxHeight;

                    // Calculate crop dimensions to maintain 2:1 aspect ratio
                    let srcX = 0;
                    let srcY = 0;
                    let srcWidth = img.width;
                    let srcHeight = img.height;

                    const srcAspectRatio = img.width / img.height;

                    if (srcAspectRatio > targetAspectRatio) {
                        // Image is wider than target ratio, crop width
                        srcWidth = img.height * targetAspectRatio;
                        srcX = (img.width - srcWidth) / 2; // Center crop
                    } else if (srcAspectRatio < targetAspectRatio) {
                        // Image is taller than target ratio, crop height
                        srcHeight = img.width / targetAspectRatio;
                        srcY = (img.height - srcHeight) / 2; // Center crop
                    }

                    canvas.width = targetWidth;
                    canvas.height = targetHeight;

                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error('Failed to get canvas context'));
                        return;
                    }

                    // Use better image smoothing
                    ctx.imageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';

                    // Draw the cropped and resized image
                    ctx.drawImage(
                        img,
                        srcX, srcY, srcWidth, srcHeight, // Source (crop)
                        0, 0, targetWidth, targetHeight   // Destination (resize)
                    );

                    // Convert to blob with quality setting
                    canvas.toBlob((blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Failed to convert canvas to blob'));
                        }
                    }, file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.85);
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = e.target?.result as string;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    const handleFileSelect = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setError(translate('resources.messages.errors.invalidFileType'));
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            setError(translate('resources.messages.errors.fileTooLarge'));
            return;
        }

        setError(null);
        setUploading(true);

        try {
            // Resize image to 800x400 for mobile banner images (2:1 aspect ratio)
            const resizedBlob = await resizeImage(file, 800, 400);

            // Create FormData
            const formData = new FormData();
            const resizedFile = new File([resizedBlob], file.name, { type: file.type });
            formData.append('file', resizedFile);
            formData.append('type', 'message');

            // Get auth token
            const token = await tokenManager.getAccessToken();
            if (!token) {
                throw new Error('No authentication token');
            }

            // Upload to backend API
            const response = await fetch(`${API_CONFIG.baseURL}/api/backend/messages/upload-image`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Upload failed');
            }

            const data = await response.json();
            if (data.url) {
                setPreview(data.url);
                onChange(data.url);
                notify('resources.messages.notifications.imageUploaded', { type: 'success' });
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            setError(translate('resources.messages.errors.uploadFailed'));
            notify('resources.messages.errors.uploadFailed', { type: 'error' });
        } finally {
            setUploading(false);
        }
    }, [onChange, notify, translate]);

    const handleRemove = useCallback(() => {
        setPreview(null);
        onChange(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [onChange]);

    return (
        <Box sx={{ width: '100%' }}>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                id={`${source}-file-input`}
            />

            <Typography variant="body1" sx={{ mb: 1, fontWeight: 500 }}>
                {label || translate('resources.messages.fields.image')}
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {preview ? (
                <Card sx={{ position: 'relative', mb: 1 }}>
                    <CardMedia
                        component="img"
                        height="200"
                        image={preview}
                        alt="Message banner"
                        sx={{ objectFit: 'cover' }}
                    />
                    <IconButton
                        onClick={handleRemove}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.7)',
                            }
                        }}
                        size="small"
                    >
                        <Close />
                    </IconButton>
                    <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                        <Typography variant="caption" color="text.secondary">
                            {translate('resources.messages.imagePreview')}
                        </Typography>
                    </Box>
                </Card>
            ) : (
                <Paper
                    sx={{
                        p: 4,
                        textAlign: 'center',
                        bgcolor: 'grey.50',
                        border: '2px dashed',
                        borderColor: 'grey.300',
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s',
                        '&:hover': {
                            borderColor: uploading ? 'grey.300' : 'primary.main',
                            bgcolor: uploading ? 'grey.50' : 'grey.100',
                        }
                    }}
                    onClick={() => !uploading && fileInputRef.current?.click()}
                >
                    {uploading ? (
                        <CircularProgress size={40} />
                    ) : (
                        <>
                            <ImageIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
                            <Typography variant="body1" gutterBottom>
                                {translate('resources.messages.clickToUpload')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {translate('resources.messages.imageRequirements') || 'PNG, JPG up to 5MB (2:1 aspect ratio)'}
                            </Typography>
                        </>
                    )}
                </Paper>
            )}

            {helperText && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    {helperText}
                </Typography>
            )}

            {!preview && !uploading && (
                <Button
                    variant="outlined"
                    startIcon={<CloudUpload />}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{ mt: 2 }}
                    fullWidth
                >
                    {translate('resources.messages.selectImage')}
                </Button>
            )}
        </Box>
    );
};

export default ImageUploadField;